import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Auth.css';
import { API_URL } from '../../config/api';
import { showError, showSuccess } from '../../utils/toast';
import { getAuthHeader, unwrapApiPayload } from '../../utils/auth';
import { avatarSrc } from '../../utils/avatar';
import { getStoredUser, setStoredUser } from '../../super-admin/auth';
import PhoneField, { EMPTY_PHONE, channelLabel, formatPhone, phoneErrorFrom, toE164Phone } from './PhoneField';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brand/tracktcrm-auth-logo.png`;
const OTP_LENGTH = 6;
const emptyOtp = () => new Array(OTP_LENGTH).fill('');

const initialsOf = (name, email) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  const letters = words.length ? words.slice(0, 2).map((w) => w[0]) : [String(email || '?')[0]];
  return letters.join('').toUpperCase();
};

const splitName = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  return { first: words[0] || '', last: words.slice(1).join(' ') };
};

const GoogleMark = () => (
  <svg width="26" height="26" viewBox="0 0 18 18" aria-label="Google" role="img">
    <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88z" />
    <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z" />
    <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z" />
    <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.4 7.49a4.77 4.77 0 0 1 4.58-3.3z" />
  </svg>
);

const errorText = (data, fallback) => {
  const msg = data?.message ?? data?.error;
  if (Array.isArray(msg)) return msg[0] || fallback;
  return typeof msg === 'string' && msg ? msg : fallback;
};

function CompleteProfile() {
  const navigate = useNavigate();
  const user = getStoredUser() || {};
  const [otpEnabled, setOtpEnabled] = useState(null);
  const [channel, setChannel] = useState('sms');
  const [firstName, setFirstName] = useState(() => splitName(user.name).first);
  const [lastName, setLastName] = useState(() => splitName(user.name).last);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [phone, setPhone] = useState(EMPTY_PHONE);
  const [phoneError, setPhoneError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState('phone');
  const [sentTo, setSentTo] = useState('');
  const [otp, setOtp] = useState(emptyOtp);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    fetch(`${API_URL}/auth/config`)
      .then((res) => (res.ok ? res.json() : {}))
      .then(unwrapApiPayload)
      .then((data) => {
        setOtpEnabled(data?.phoneOtpEnabled === true);
        if (data?.phoneOtpChannel) setChannel(data.phoneOtpChannel);
      })
      .catch(() => setOtpEnabled(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/auth/me`, { headers: getAuthHeader() })
      .then((res) => (res.ok ? res.json() : null))
      .then(unwrapApiPayload)
      .then((data) => {
        if (cancelled || !data?.user || data.user.phoneSetupRequired) return;
        setStoredUser(data.user);
        navigate('/dashboard', { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'otp') inputRefs.current[0]?.focus();
  }, [step]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  const post = async (path, body) => {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body),
    });
    const data = unwrapApiPayload(await res.json().catch(() => ({})));
    if (res.status === 401) {
      showError('Your session has expired. Please log in again.');
      logout();
      return null;
    }
    if (!res.ok) {
      const err = new Error(errorText(data, 'Something went wrong. Please try again.'));
      err.phoneError = phoneErrorFrom(data);
      throw err;
    }
    return data;
  };

  const showFailure = (err) => {
    if (err.phoneError) {
      setPhoneError(err.phoneError);
      setStep('phone');
    }
    showError(err.message);
  };

  const names = () => ({ firstName: firstName.trim(), lastName: lastName.trim() });

  const finish = (data) => {
    if (data?.user) setStoredUser(data.user);
    showSuccess('Your account is ready.');
    navigate('/dashboard', { replace: true });
  };

  const sendCode = async (to) => {
    setBusy(true);
    try {
      const data = await post('/auth/phone/send-otp', { phone: to });
      if (!data) return;
      setSentTo(data.phone || to);
      if (data.channel) setChannel(data.channel);
      setOtp(emptyOtp());
      setCooldown(Number(data.resendInSeconds) || 60);
      setStep('otp');
      showSuccess(`We sent a 6-digit code ${channelLabel(data.channel || channel)}.`);
    } catch (err) {
      showFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) {
      showError('Please enter your first name.');
      return;
    }
    const e164 = toE164Phone(phone);
    if (!e164) {
      showError('Please enter a valid mobile number.');
      return;
    }
    if (!agreed) {
      showError('Please accept the Terms of Service and Privacy Policy.');
      return;
    }
    if (otpEnabled) {
      await sendCode(e164);
      return;
    }
    setBusy(true);
    try {
      const data = await post('/auth/phone/save', { phone: e164, ...names() });
      if (data) finish(data);
    } catch (err) {
      showFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code) => {
    if (code.length !== OTP_LENGTH) {
      showError('Please enter the complete 6-digit code.');
      return;
    }
    setBusy(true);
    try {
      const data = await post('/auth/phone/verify', { phone: sentTo, code, ...names() });
      if (data) finish(data);
    } catch (err) {
      showFailure(err);
      setOtp(emptyOtp());
      inputRefs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const handleDigit = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
    if (value && index === OTP_LENGTH - 1 && next.join('').length === OTP_LENGTH) verify(next.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pasted.length !== OTP_LENGTH) return;
    e.preventDefault();
    setOtp(pasted.split(''));
    inputRefs.current[OTP_LENGTH - 1]?.focus();
    verify(pasted);
  };

  const displayName = String(user.name || '').trim() || 'there';

  return (
    <div className="auth-container">
      <div className="auth-form-section profile-section">
        <div className="auth-form-container profile-card">
          <div className="profile-card-top">
            <a href="https://tracktcrm.com/" target="_blank" rel="noopener noreferrer" aria-label="TracktCRM">
              <img src={LOGO_SRC} alt="TracktCRM" className="profile-card-logo" />
            </a>
            <span className="profile-card-times" aria-hidden>×</span>
            <GoogleMark />
          </div>

          <div className="profile-hero">
            {avatarSrc(user.avatar) && !avatarFailed ? (
              <img
                className="profile-avatar profile-avatar-photo"
                src={avatarSrc(user.avatar)}
                alt={displayName}
                referrerPolicy="no-referrer"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <div className="profile-avatar" aria-hidden>{initialsOf(user.name, user.email)}</div>
            )}
            <div className="profile-hero-text">
              <h1>Welcome {displayName}!</h1>
              {user.email ? (
                <p>
                  A new TracktCRM account will be created for the email address <strong>{user.email}</strong>.
                </p>
              ) : null}
            </div>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <div className="profile-name-row">
                <div className="fl-field">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value.slice(0, 60))}
                    autoComplete="given-name"
                    disabled={busy}
                    required
                  />
                </div>
                <div className="fl-field">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value.slice(0, 60))}
                    autoComplete="family-name"
                    disabled={busy}
                  />
                </div>
              </div>

              <PhoneField
                id="phone"
                label="Mobile Number"
                variant="boxed"
                className="profile-phone"
                value={phone}
                onChange={(next) => {
                  setPhone(next);
                  setPhoneError('');
                }}
                error={phoneError}
                disabled={busy}
              />

              <p className="profile-note">
                {otpEnabled
                  ? `We'll send a 6-digit code ${channelLabel(channel)} to verify this number.`
                  : 'We use this number for account security and support.'}
              </p>

              <label className="auth-agree">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
                <span>
                  I agree to the{' '}
                  <a href="https://tracktcrm.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="https://tracktcrm.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </span>
              </label>

              <button type="submit" className="auth-button profile-submit" disabled={busy || otpEnabled === null}>
                {busy ? 'Please wait...' : 'Create Account'}
              </button>
            </form>
          ) : (
            <div className="otp-form">
              <p className="profile-note">
                Enter the 6-digit code we sent {channelLabel(channel)} to <strong>{formatPhone(sentTo)}</strong>.{' '}
                <button type="button" className="resend-button" onClick={() => setStep('phone')} disabled={busy}>
                  Change number
                </button>
              </p>

              <div className="otp-inputs">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className="otp-input"
                    aria-label={`Digit ${index + 1}`}
                    value={digit}
                    onChange={(e) => handleDigit(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={busy}
                  />
                ))}
              </div>

              <button
                type="button"
                className="auth-button profile-submit"
                onClick={() => verify(otp.join(''))}
                disabled={busy || otp.join('').length !== OTP_LENGTH}
              >
                {busy ? 'Verifying...' : 'Verify & Create Account'}
              </button>

              <div className="otp-info">
                <span>If the code has expired, ask for a new one below.</span>
              </div>

              <div className="resend-section">
                <span>Didn't get the code?</span>
                {cooldown > 0 ? (
                  <span className="resend-timer">Resend in {cooldown}s</span>
                ) : (
                  <button type="button" className="resend-button" onClick={() => sendCode(sentTo)} disabled={busy}>
                    Resend code
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="auth-footer">
            <p>
              Not you?{' '}
              <button type="button" className="resend-button" onClick={logout}>
                Log out
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompleteProfile;
